import React, { useState } from 'react';
import GoogleSignin from './GoogleSignin';
import FacebookLogin from './FacebookLogin';
import api from 'config/axiosConfig';
import { useUser } from 'util/UserContext';
import Modal from 'ui/Modal';
import LocalSignin from './LocalSignin';
import PasswordRecovery from './PasswordRecovery';
import EmailVerificationModal from './EmailVerificationModal';
import { Info } from 'lucide-react';
import OTPLogin from './OTPLogin';
import AuthService from 'services/authService';

export type AuthView = 'auth' | 'forgotPassword' | 'emailVerification' | 'otpLogin';

interface AuthModalProps {
  show: boolean;
  onClose: () => void;
  passwordReset?: boolean;
}

const AuthModal: React.FC<AuthModalProps> = ({ show, onClose, passwordReset }) => {
  const { refreshUser, setShowUsernameModal, setShowUserOnboardModal } = useUser();

  // Use an internal view state for the flow.
  const [activeView, setActiveView] = useState<AuthView>(
    passwordReset ? 'forgotPassword' : 'auth'
  );
  const [authError, setAuthError] = useState<string>('');
  // store email for forgot password and verification
  const [emailForReset, setEmailForReset] = useState('');
  const [emailForVerification, setEmailForVerification] = useState('');

  // Callbacks for social login remain essentially the same.
  const handleCredentialResponse = async (response: any): Promise<void> => {
    try {
      const { credential } = response;
      const result = await api.post('/auth/google/callback', { token: credential });
      const userStatus = result.status;
      // Store the access token
      if (result.data.accessToken) {
        AuthService.setAccessToken(result.data.accessToken);
      }
      await refreshUser();
      if (userStatus === 201) {
        onClose();
        setShowUsernameModal(true);
      } else if (userStatus === 200) {
        onClose();
      } else {
        setAuthError('Failed to authenticate. Please try again.');
      }
    } catch (error) {
      console.error('Error handling credential response:', error);
      setAuthError('Failed to authenticate. Please try again.');
    }
  };

  const handleFacebookResponse = async (response: any): Promise<void> => {
    if (response.status !== 'connected') {
      if (window.FB) {
        window.FB.login((loginResp: any) => {
          if (loginResp.authResponse) {
            handleFacebookResponse(loginResp);
          } else {
            setAuthError('User canceled or did not fully authorize Facebook.');
          }
        }, { scope: 'public_profile,email' });
      } else {
        return;
      }
    }
    const { accessToken, userID } = response.authResponse;
    try {
      const result = await api.post('/auth/facebook/callback', { accessToken, userID });
      const userStatus = result.status;
      if (result.data.accessToken) {
        AuthService.setAccessToken(result.data.accessToken);
      }
      await refreshUser();
      if (userStatus === 201) {
        onClose();
        setShowUsernameModal(true);
      } else if (userStatus === 200) {
        onClose();
      } else {
        setAuthError('Failed to authenticate. Please try again.');
      }
    } catch (error: any) {
      console.error('Error handling Facebook response:', error);
      if (error.response?.status === 401 && window.FB) {
        window.FB.login((loginResp: any) => {
          if (loginResp.authResponse) {
            handleFacebookResponse(loginResp);
          } else {
            setAuthError('User canceled or did not fully authorize Facebook.');
          }
        }, { scope: 'public_profile,email' });
      } else {
        setAuthError('Failed to authenticate. Please try again.');
      }
    }
  };

  // Callback from LocalSignin when the user clicks "Forgot password?".
  const handleForgotPassword = (email: string) => {
    setEmailForReset(email);
    setActiveView('forgotPassword');
  };

  const handleUseCode = () => {
    setActiveView('otpLogin');
  };

  // Callback from LocalSignin when registration is successful.
  const handleRegistrationSuccess = (email: string) => {
    setEmailForVerification(email);
    setActiveView('emailVerification');
  };

  // The modal's back arrow works only for the forgot password view.
  const handleBack = () => {
    if (activeView === 'forgotPassword') {
      setActiveView('auth');
    }
    // Optionally, you could allow "emailVerification" to go back, too.
  };

  const handleClose = () => {
    // Reset all flow state when closing.
    setActiveView('auth');
    setEmailForReset('');
    setEmailForVerification('');
    setAuthError('');
    onClose();
  };

  return (
    <Modal
      isOpen={show}
      onClose={handleClose}
      title={
        activeView === 'forgotPassword'
          ? 'Reset your password'
          : activeView === 'emailVerification'
          ? 'Verify your email'
          : 'Start spilling the tea'
      }
      showBackButton={activeView === 'forgotPassword' || activeView === 'otpLogin'}
      onBack={handleBack}
    >
      <div className="flex flex-col h-[calc(100%-44px)] max-h-[calc(100%-44px)] sm:h-auto overflow-y-scroll sm:overflow-y-auto space-y-4">
        {activeView === 'auth' && (
          <div className="flex flex-col flex-grow space-y-4">
            <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
              By continuing, you agree to our{' '}
              <a
                href="https://www.societea.io/policies/user-agreement"
                target="_blank"
                rel="noopener noreferrer"
                className="external-link"
              >
                User Agreement
              </a>{' '}
              and acknowledge that you understand the{' '}
              <a href="/policies/privacy-policy" className="external-link">
                Privacy Policy
              </a>
              .
            </p>

            {authError && (
              <div className="flex items-center text-warning-red dark:text-warning-red-dark text-sm">
                <Info className="mr-1 size-4" />
                <span>{authError}</span>
              </div>
            )}

            <div className="flex flex-col space-y-4 w-full mb-4 mx-auto items-center justify-center">
              <GoogleSignin handleCredentialResponse={handleCredentialResponse} />
              <FacebookLogin handleFacebookResponse={handleFacebookResponse} />
            </div>

            <LocalSignin
              onClose={onClose}
              onForgotPassword={handleForgotPassword}
              onUseCode={handleUseCode}
              onRegistrationSuccess={handleRegistrationSuccess}
              initialEmail=""
                renderButtons={({ isSubmitting, isRegistering, isButtonDisabled, onSubmit }) => (
                <div className="flex flex-col pb-4 mt-auto sm:flex sm:flex-row sm:justify-end sm:space-x-2">
                    <button
                    type="button"
                    className="btn-secondary w-full sm:w-fit py-2 px-4"
                    onClick={onClose}
                    >
                    Cancel
                    </button>
                    <button
                    type="submit"
                    disabled={isButtonDisabled}
                    onClick={onSubmit}
                    className={`w-full sm:w-fit py-2 px-4 ${
                        isButtonDisabled ? 'btn-primary-disabled' : 'btn-primary'
                    } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                    {isSubmitting
                        ? isRegistering
                        ? 'Signing Up...'
                        : 'Logging In...'
                        : isRegistering
                        ? 'Sign Up'
                        : 'Log In'}
                    </button>
                </div>
                )}
            />
          </div>
        )}

        {activeView === 'forgotPassword' && (
          <PasswordRecovery
            email={emailForReset}
            onBack={() => setActiveView('auth')}
            onClose={handleClose}
          />
        )}

        {activeView === 'emailVerification' && (
          <EmailVerificationModal 
            isOpen={true} 
            onClose={() => {
              handleClose();
              setShowUserOnboardModal(true);
            }} 
            email={emailForVerification} 
          />
        )}

        {activeView === 'otpLogin' && (
          <OTPLogin
            isOpen={true}
            onClose={handleClose}
          />
        )}
      </div>

    </Modal>
  );
};

export default AuthModal;
